import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { smartFactureAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../components/ui/use-toast';
import TrialPaywall from '../../components/trial/TrialPaywall';
import useTrialGuard from '../../hooks/useTrialGuard';
import { 
  Camera, Upload, FileText, Loader2, CheckCircle, 
  AlertTriangle, ArrowLeft, Sparkles, X, Eye,
  Building2, Calendar, Euro, Receipt, ArrowRight
} from 'lucide-react';

const ScannerPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  
  const [scanning, setScanning] = useState(false);
  const [converting, setConverting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [currentScanId, setCurrentScanId] = useState(null);

  // Trial guard hook
  const { 
    showPaywall, 
    paywallReason, 
    paywallAction, 
    closePaywall, 
    handleTrialError 
  } = useTrialGuard();

  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Format non supporté',
        description: 'Veuillez utiliser une image JPEG, PNG ou WebP',
        variant: 'destructive'
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);

    // Upload and analyze
    setScanning(true);
    setScanResult(null);
    setEditedData(null);

    try {
      const response = await smartFactureAPI.uploadScan(file);
      
      if (response.data.success) {
        setScanResult(response.data);
        setEditedData(response.data.invoice_data);
        setCurrentScanId(response.data.scan_id);
        
        toast({
          title: 'Document analysé',
          description: 'Les informations ont été extraites avec succès',
        });
      }
    } catch (error) {
      // Check if it's a trial limit error
      if (handleTrialError(error, 'scan_upload')) {
        setPreviewUrl(null); // Reset preview
        return; // Paywall will be shown by the hook
      }
      
      toast({
        title: 'Erreur d\'analyse',
        description: error.response?.data?.detail?.message || error.response?.data?.detail || 'Échec de l\'analyse du document',
        variant: 'destructive'
      });
    } finally {
      setScanning(false);
    }
  }, [toast, handleTrialError]);

  const handleConvertToInvoice = async () => {
    if (!currentScanId) return;
    
    setConverting(true);
    try {
      const response = await smartFactureAPI.convertScanToInvoice(currentScanId);
      
      toast({
        title: 'Facture créée',
        description: `Facture ${response.data.invoice_number} créée en brouillon`,
      });
      
      navigate('/dashboard/smartfacture');
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Échec de la conversion en facture',
        variant: 'destructive'
      });
    } finally {
      setConverting(false);
    }
  };

  const resetScan = () => {
    setPreviewUrl(null);
    setScanResult(null);
    setEditedData(null);
    setCurrentScanId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateEditedField = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.9) {
      return <Badge className="bg-emerald-100 text-emerald-700">Haute confiance</Badge>;
    } else if (confidence >= 0.7) {
      return <Badge className="bg-amber-100 text-amber-700">Confiance moyenne</Badge>;
    }
    return <Badge className="bg-red-100 text-red-700">Vérification requise</Badge>;
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="scanner-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard/smartfacture')}
            className="text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Scanner intelligent</h1>
            <p className="text-slate-500 text-sm">Digitalisez vos documents avec l'IA</p>
          </div>
        </div>
        <Badge variant="outline" className="flex items-center space-x-1.5 px-3 py-1.5">
          <Sparkles className="h-3.5 w-3.5 text-violet-500" />
          <span>OCR IA</span>
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left - Upload Zone */}
        <div className="space-y-6">
          {!previewUrl ? (
            <Card className="border-2 border-dashed border-slate-300 hover:border-blue-400 transition-colors">
              <CardContent className="p-12">
                <div 
                  className="flex flex-col items-center justify-center text-center cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                    <Camera className="h-10 w-10 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Scanner un document
                  </h3>
                  <p className="text-slate-500 mb-4 max-w-xs">
                    Importez une photo de facture, ticket ou devis pour extraction automatique
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      type="button"
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="upload-button"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Choisir un fichier
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-4">
                    Formats acceptés: JPEG, PNG, WebP
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200">
              <CardHeader className="pb-3 border-b flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center space-x-2">
                  <Eye className="h-5 w-5 text-blue-600" />
                  <span>Document scanné</span>
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetScan}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Nouveau scan
                </Button>
              </CardHeader>
              <CardContent className="p-4">
                <div className="relative rounded-lg overflow-hidden bg-slate-100">
                  <img 
                    src={previewUrl} 
                    alt="Document scanné" 
                    className="w-full h-auto max-h-[500px] object-contain"
                  />
                  {scanning && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                      <div className="text-center">
                        <Loader2 className="h-10 w-10 text-blue-600 animate-spin mx-auto mb-3" />
                        <p className="text-slate-700 font-medium">Analyse en cours...</p>
                        <p className="text-slate-500 text-sm">Extraction des données par IA</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scan History Link */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <Button
                variant="outline"
                className="w-full justify-between"
                onClick={() => navigate('/dashboard/smartfacture/scans/history')}
              >
                <span className="flex items-center">
                  <Receipt className="h-4 w-4 mr-2 text-slate-500" />
                  Historique des scans
                </span>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right - Extracted Data */}
        <div className="space-y-6">
          {scanResult ? (
            <>
              {/* Confidence indicator */}
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {scanResult.extracted_data?.confidence >= 0.7 ? (
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">Extraction terminée</p>
                        <p className="text-sm text-slate-500">
                          Type: {scanResult.extracted_data?.document_type || 'Document'}
                        </p>
                      </div>
                    </div>
                    {getConfidenceBadge(scanResult.extracted_data?.confidence || 0)}
                  </div>
                </CardContent>
              </Card>

              {/* Extracted fields - Editable */}
              <Card className="border-slate-200">
                <CardHeader className="pb-3 border-b">
                  <CardTitle className="text-base flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <span>Données extraites</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Supplier */}
                  <div>
                    <Label className="text-slate-600 text-sm flex items-center space-x-1">
                      <Building2 className="h-3.5 w-3.5" />
                      <span>Fournisseur</span>
                    </Label>
                    <Input
                      value={editedData?.client_name || ''}
                      onChange={(e) => updateEditedField('client_name', e.target.value)}
                      className="mt-1"
                      placeholder="Nom du fournisseur"
                    />
                  </div>

                  {/* SIREN */}
                  <div>
                    <Label className="text-slate-600 text-sm">SIREN</Label>
                    <Input
                      value={editedData?.client_siren || ''}
                      onChange={(e) => updateEditedField('client_siren', e.target.value)}
                      className="mt-1"
                      placeholder="123 456 789"
                    />
                  </div>

                  {/* Invoice number & date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm flex items-center space-x-1">
                        <Receipt className="h-3.5 w-3.5" />
                        <span>N° facture</span>
                      </Label>
                      <Input
                        value={scanResult.extracted_data?.invoice_info?.number || ''}
                        className="mt-1"
                        disabled
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm flex items-center space-x-1">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Date</span>
                      </Label>
                      <Input
                        type="date"
                        value={editedData?.invoice_date || ''}
                        onChange={(e) => updateEditedField('invoice_date', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Amounts */}
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <h4 className="font-medium text-slate-700 flex items-center space-x-1">
                      <Euro className="h-4 w-4" />
                      <span>Montants</span>
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-slate-500 text-xs">Total HT</Label>
                        <p className="font-semibold text-slate-900">
                          {scanResult.extracted_data?.amounts?.subtotal_ht?.toFixed(2) || '0.00'} €
                        </p>
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">TVA</Label>
                        <p className="font-semibold text-slate-900">
                          {scanResult.extracted_data?.amounts?.total_vat?.toFixed(2) || '0.00'} €
                        </p>
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Total TTC</Label>
                        <p className="font-semibold text-blue-600">
                          {scanResult.extracted_data?.amounts?.total_ttc?.toFixed(2) || '0.00'} €
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Items preview */}
                  {scanResult.extracted_data?.items?.length > 0 && (
                    <div>
                      <Label className="text-slate-600 text-sm mb-2 block">
                        Articles détectés ({scanResult.extracted_data.items.length})
                      </Label>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {scanResult.extracted_data.items.map((item, idx) => (
                          <div key={idx} className="text-sm p-2 bg-slate-50 rounded">
                            <p className="text-slate-700 truncate">{item.description}</p>
                            <p className="text-slate-500 text-xs">
                              {item.quantity} × {item.unit_price?.toFixed(2)} € = {item.total_ht?.toFixed(2)} €
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Warnings */}
                  {scanResult.extracted_data?.warnings?.length > 0 && (
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="text-amber-800 text-sm font-medium mb-1">Avertissements</p>
                      <ul className="text-amber-700 text-xs space-y-1">
                        {scanResult.extracted_data.warnings.map((warning, idx) => (
                          <li key={idx}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={handleConvertToInvoice}
                  disabled={converting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 h-11"
                  data-testid="convert-to-invoice-btn"
                >
                  {converting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  Créer une facture
                </Button>
                <Button
                  variant="outline"
                  onClick={resetScan}
                  className="h-11"
                >
                  Nouveau scan
                </Button>
              </div>
            </>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-700 mb-2">
                  Aucun document analysé
                </h3>
                <p className="text-slate-500 text-sm max-w-xs mx-auto">
                  Importez une photo de facture ou de ticket pour extraire automatiquement les informations
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Trial Paywall Modal */}
      <TrialPaywall 
        isOpen={showPaywall} 
        onClose={closePaywall}
        reason={paywallReason}
        actionType={paywallAction}
      />
    </div>
  );
};

export default ScannerPage;
